import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createBlock as _createBlock, withCtx as _withCtx, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, Fragment as _Fragment } from "vue"

import { ref, computed, inject, onMounted, watch, PropType } from "vue";
import FindContactModal from "@/components/find/contacts/find-contact-modal.vue";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import { IDDescriptionPair } from "@/types/generic/IDDescriptionPair";
import { ContactFindResult } from "@/types/find/contacts/ContactFindResult";
import { DxButton, DxTextBox } from "devextreme-vue/text-box";
import contactsPeekPopup from "@/components/contacts/contacts-peek-popup.vue";
import { FieldSize } from "@/enums/system/FieldSize";
import { RouteNames } from "@/router/RouteNames";
import { useRouter } from "vue-router";
import { DxValidator } from "devextreme-vue";
import { DxRequiredRule } from "devextreme-vue/validator";


export default /*#__PURE__*/_defineComponent({
  __name: 'contact-lookup',
  props: {
  /**
   * The numeric value used with `v-model` to bind the component state. This typically represents an ID or a selected value.
   * @type {number}
   * @required
   */
  modelValue: {
    type: Number,
    required: true,
  },
  /**
   * Determines whether the control is read only.
   * @type {boolean}
   * @default true
   */
  readOnly: {
    type: Boolean,
    required: true,
  },
  /**
   * The label displayed for the control.
   * @type {String}
   * @default ''
   */
  label: {
    type: String,
    default: "",
  },
  /**
   * Restricts the types of contacts that can be found.
   * @type {number}
   * @default 0
   */
  findTypeRestriction: {
    type: Number,
    default: 0,
  },
  /**
   * Determines whether to find name by Id.
   * @type {boolean}
   * @default false
   */
  findNameOnIdChange: {
    type: Boolean,
    default: false,
  },
  /**
   * Determines whether the control is required.
   * @type {boolean}
   * @default false
   */
  isRequired: {
    type: Boolean,
    default: false,
  },
  /**
   * The unique identifier for the current contact.
   * @type {number}
   * @default: 0
   */
  currentContactId: {
    type: Number,
    default: 0,
  },
  /**
   * The width of the field, defined by the `FieldSize` enum.
   * @type {FieldSize}
   * @default FieldSize.Full
   */
  width: {
    type: String as PropType<FieldSize>,
    default: FieldSize.Full,
  },
  /**
   * Determines whether to show clear icon.
   * @type {boolean}
   * @default false
   */
  showClearIcon: {
    type: Boolean,
    default: false,
  },
  /**
   * Specifies the type of contact to search for.
   * @type {number}
   * @default -1
   */
  typeToFind: {
    type: Number,
    default: -1,
  },
  /**
   * Determines whether to show navigate contact icon.
   * @type {boolean}
   * @default true
   */
  showNavigateContactIcon: {
    type: Boolean,
    default: true,
  },
  /**
   * The validation group name used for form validation.
   * @type {string}
   * @default ''
   */
  validationGroup: {
    type: String,
    required: false,
    default: "",
  },
  /**
   * The field name which will be used to disable contacts from grid.
   * @type {string}
   * @default ''
   */
  restrictedFieldName: {
    type: String,
    default: "",
  },
  /**
   * The fixed width of the field which defined manually.
   * @type Number
   * @default 0
   */
  fixedWidth: {
    type: Number,
    default: 0,
  },
  /**
   * Determines whether to display Peek.
   * @type {boolean}
   * @default true
   */
  displayPeek: {
    type: Boolean,
    default: true,
  },
  /**
   * Controls the visibility of the "New Contact" button.
   * @type {Boolean}
   * @default false (button is hidden)
   */
  showNewContactButton: {
    type: Boolean,
    default: false,
  },
  /**
   * Controls whether to fetch the name when the component is mounted based on the initial `modelValue`.
   * If set to false, the name will not be fetched until the `modelValue` changes.
   * @type {Boolean}
   * @default false
   */
  fetchInitialName: {
    type: Boolean,
    default: true,
  },
},
  emits: [
  /**
   * Triggered when the `modelValue` changes. This is used to update the v-model variable in the parent component.
   * @property {boolean} value - Indicates whether the modal has been opened or closed.
   */
  "update:modelValue",
  /**
   * Triggered when a contact is selected from the search results.
   * @param {ContactFindResult} contact - The details of the selected contact.
   */
  "contactSelected",
  /**
   * Triggered when the user clicks to navigate to the selected contact's details page.
   */
  "navigateToContact",
  /**
   * Triggered when the user clicks to open the new contact creation modal.
   * Used to initiate the creation of a new contact.
   */
  "openNewContactModal",
],
  setup(__props, { emit }) {

const props = __props;





const contactId = computed({
  get: () => props.modelValue,
  set: (value: number) => emit("update:modelValue", value),
});

const apiClient = inject("AcquaintApiClient") as AcquaintApiClient;
const showModal = ref(false);
const contactName = ref("");
const showPeek = ref(false);
const contactFullDetails = ref<ContactFindResult>();
const peekModalKey = ref(0);
const router = useRouter();

onMounted(async () => {
  if (!props.fetchInitialName) {
    return;
  }
  await getContactName();
});

// Watch contactId changes and update contactName accordingly
watch(
  () => contactId.value,
  async (newValue, oldValue) => {
    if (newValue !== oldValue) {
      if (!newValue) {
        contactName.value = "";
        contactFullDetails.value = undefined;
        incrementPeekModalKey();
      } else {
        await getContactName();
      }
    }
  },
);

const incrementPeekModalKey = () => {
  peekModalKey.value++;
};

const getContactName = async () => {
  // No need to call api if no contact has been selected
  if (contactId.value === 0) {
    contactName.value = "";
    incrementPeekModalKey();
    return;
  }

  // When the component loads, populate the contact's name so user can see who is currently selected
  const contact: IDDescriptionPair = await apiClient.httpGet(
    `app/contacts/${contactId.value}/name`,
  );
  contactName.value = contact.description;
  incrementPeekModalKey();
};

/** Handles the contact selected event of the find contact modal */
const onContactSelected = async (e: ContactFindResult) => {
  if (!e) {
    return;
  }
  contactName.value = e.name;
  incrementPeekModalKey();
  contactId.value = e.id;
  contactFullDetails.value = e;
  emit("contactSelected", e);
};

const onPrimaryContactSelected = (primaryId: number, primaryName: string) => {
  contactName.value = primaryName;
  incrementPeekModalKey();
  contactId.value = primaryId;
};

const searchClick = () => {
  showModal.value = true;
};

const clearClick = () => {
  contactName.value = "";
  contactId.value = 0;
  contactFullDetails.value = undefined;
  emit("contactSelected", undefined);
};

const navigateToContact = () => {
  emit("navigateToContact");
  setTimeout(
    () =>
      router.push({
        name: RouteNames.ContactPage,
        params: { contactid: contactId.value.toString() },
      }),
    500,
  );
};

const openNewContactModal = () => {
  emit("openNewContactModal");
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", null, [
      (__props.label)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: _normalizeClass(["label", { asteriskVisible: props.isRequired }])
          }, _toDisplayString(__props.label), 3))
        : _createCommentVNode("", true),
      _createElementVNode("div", {
        class: "contact-lookup-container",
        "data-bind": "dxTextBox: {}",
        style: _normalizeStyle({ '--fixed-width': __props.fixedWidth > 0 ? __props.fixedWidth + 'px' : '100%' })
      }, [
        _createVNode(_unref(DxTextBox), {
          value: contactName.value,
          "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((contactName).value = $event)),
          "read-only": true,
          width: props.width
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(DxButton), {
              name: "clear-text",
              position: "after",
              options: {
            icon: 'clear',
            stylingMode: 'text',
            type: 'dx-show-clear-button',
            visible: props.showClearIcon && contactId.value > 0 && !props.readOnly,
            disabled: false,
            onClick: () => clearClick(),
          }
            }, null, 8, ["options"]),
            _createVNode(_unref(DxButton), {
              name: "navigate-to-contact",
              position: "after",
              options: {
            icon: 'fas fa-user',
            stylingMode: 'text',
            type: 'dx-show-clear-button',
            visible: contactId.value > 0 && props.showNavigateContactIcon,
            disabled: false,
            onClick: () => navigateToContact(),
          }
            }, null, 8, ["options"]),
            _createVNode(_unref(DxButton), {
              name: "peek-contact",
              position: "after",
              options: {
            icon: 'fas fa-eye',
            stylingMode: 'text',
            type: 'dx-dropdowneditor-button',
            disabled: false,
            visible: contactId.value > 0 && props.displayPeek,
            onClick: () => (showPeek.value = true),
          }
            }, null, 8, ["options"]),
            _createVNode(_unref(DxButton), {
              name: "contact-lookup",
              position: "after",
              options: {
            icon: 'fas fa-search',
            stylingMode: 'text',
            type: 'dx-dropdowneditor-button',
            disabled: false,
            visible: !props.readOnly,
            onClick: () => searchClick(),
          }
            }, null, 8, ["options"]),
            _createVNode(_unref(DxValidator), {
              "validation-group": props.validationGroup
            }, {
              default: _withCtx(() => [
                (props.isRequired)
                  ? (_openBlock(), _createBlock(_unref(DxRequiredRule), {
                      key: 0,
                      message: ""
                    }))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }, 8, ["validation-group"])
          ]),
          _: 1
        }, 8, ["value", "width"])
      ], 4)
    ]),
    _createVNode(FindContactModal, {
      modelValue: showModal.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showModal).value = $event)),
      onContactSelected: onContactSelected,
      findTypeRestriction: props.findTypeRestriction,
      "current-contact-id": __props.currentContactId,
      onPrimaryContactSelected: onPrimaryContactSelected,
      typeToFind: props.typeToFind,
      "restricted-field-name": __props.restrictedFieldName,
      showNewContactButton: __props.showNewContactButton,
      onOpenNewContactModal: openNewContactModal
    }, null, 8, ["modelValue", "findTypeRestriction", "current-contact-id", "typeToFind", "restricted-field-name", "showNewContactButton"]),
    (_openBlock(), _createBlock(contactsPeekPopup, {
      key: peekModalKey.value,
      modelValue: showPeek.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((showPeek).value = $event)),
      contactId: contactId.value,
      title: contactName.value
    }, null, 8, ["modelValue", "contactId", "title"]))
  ], 64))
}
}

})